import { CircuitFormData } from '@/types/circuits/CircuitFormData';
import { Control, useFormContext } from 'react-hook-form';
import { Label } from '../ui/label';
import { Fragment, useEffect, useCallback, useMemo } from 'react';
import HorizontalLine from '../ui/HorizontalLine';
import { CircuitBenefitType } from '@/types/circuits/CircuitBenefitType';
import { CircuitDayFormData } from '@/types/circuits/CircuitDaysFormData';
import CircuitEditorialBenefitElement from './CircuitEditorialBenefitElement';
import { useCircuitBenefitRemoval } from '@/hooks/useCircuitBenefitRemoval';
import { useCircuitBenefitsState } from '@/hooks/useCircuitBenefitsState';
import { useTranslation } from '@/i18n';
import { CircuitFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { SupportedLocale } from '@/types/supported-locale.type';
import {
  AssociatedBenefitStepDisplay,
  getAssociatedBenefitsForStepDisplay,
} from '@/utils/circuits/associatedBenefits/getAssociatedBenefitsForStepDisplay';

interface CircuitEditorialStepProps {
  index: number;
  stepIndex: number;
  openStepModal: (type: CircuitBenefitType) => void;
  isViewMode?: boolean;
  removeStep: () => void;
  maxBenefitStepIndex: number;
  days: CircuitDayFormData[];
  control: Control<CircuitFormDataWithTranslations>;
  language?: SupportedLocale;
}

const CircuitEditorialStep = ({
  index,
  stepIndex,
  openStepModal,
  isViewMode = false,
  removeStep,
  maxBenefitStepIndex,
  days,
  control,
  language,
}: CircuitEditorialStepProps) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext<CircuitFormData>();
  const { removeStandardBenefit, removeOptionalBenefits } =
    useCircuitBenefitRemoval(days, setValue);

  const {
    benefits,
    atLeastOneStandard,
    optionalLength,
    benefitProperties,
    optionalBenefits,
  } = useCircuitBenefitsState({
    index,
    stepIndex,
    control,
  });

  useEffect(() => {
    if (
      stepIndex !== maxBenefitStepIndex + 1 &&
      !atLeastOneStandard &&
      optionalLength === 0
    ) {
      removeStep();
    }
  }, [
    atLeastOneStandard,
    maxBenefitStepIndex,
    optionalLength,
    removeStep,
    stepIndex,
  ]);

  const standardAssociatedBenefits = useMemo(
    () =>
      getAssociatedBenefitsForStepDisplay({
        accommodationBenefit: benefits.accommodations?.standardBenefit,
        indexStepDay: benefitProperties.accommodations.indexStepDay,
        stepNbDays: benefitProperties.accommodations.stepNbDays,
      }),
    [
      benefits.accommodations?.standardBenefit,
      benefitProperties.accommodations,
    ],
  );

  // Prestations associées regroupées PAR hébergement optionnel (et par type de
  // colonne), afin d'aligner chaque prestation associée sur la même ligne que
  // l'hébergement auquel elle appartient.
  const optionalAccommodationRows = useMemo(
    () =>
      optionalBenefits.accommodations.benefits.map((benefit) => {
        const associated = getAssociatedBenefitsForStepDisplay({
          accommodationBenefit: benefit,
          indexStepDay: benefitProperties.accommodations.indexStepDay,
          stepNbDays: benefitProperties.accommodations.stepNbDays,
        });
        return {
          transportAssociated: associated.filter(
            (item) => item.columnType === 'Transport',
          ),
          activitiesAssociated: associated.filter(
            (item) => item.columnType === 'Activités',
          ),
        };
      }),
    [
      optionalBenefits.accommodations.benefits,
      benefitProperties.accommodations,
    ],
  );

  const renderAssociatedBenefits = useCallback(
    (items: AssociatedBenefitStepDisplay[], keyPrefix = '') =>
      items.map((item) => (
        <CircuitEditorialBenefitElement
          key={`${keyPrefix}${item.benefitId}`}
          isStandard
          isAssociated
          hideRemove
          disableClick
          showEditorialFlags={false}
          type={item.columnType}
          title={item.name}
          isViewMode={isViewMode}
          benefitProperties={{
            indexStepDay: item.indexStepDay,
            stepNbDays: item.stepNbDays,
          }}
        />
      )),
    [isViewMode],
  );

  const standardTransportAssociated = useMemo(
    () =>
      standardAssociatedBenefits.filter(
        (item) => item.columnType === 'Transport',
      ),
    [standardAssociatedBenefits],
  );

  const standardActivitiesAssociated = useMemo(
    () =>
      standardAssociatedBenefits.filter(
        (item) => item.columnType === 'Activités',
      ),
    [standardAssociatedBenefits],
  );

  const getStandardBenefitNameFromBenefitType = useCallback(
    (benefitType: keyof typeof benefits) => {
      if (!benefits[benefitType]?.standardBenefit) return '';
      return language
        ? (benefits[benefitType].standardBenefit.translations?.[language]
            ?.name ?? benefits[benefitType].standardBenefit.name)
        : benefits[benefitType].standardBenefit.name;
    },
    [benefits, language],
  );

  const StandardBenefitsSection = atLeastOneStandard ? (
    <>
      <HorizontalLine />
      <Label className="text-gray-700">
        {t('circuits.editorial.sections.standard')}
      </Label>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="flex flex-col gap-2">
          {benefits.transport?.standardBenefit && (
            <CircuitEditorialBenefitElement
              isStandard
              type="Transport"
              title={getStandardBenefitNameFromBenefitType('transport')}
              onRemove={() =>
                removeStandardBenefit({
                  type: 'Transport',
                  stepId: benefitProperties.transport.id,
                })
              }
              onLabelClick={() => openStepModal('Transport')}
              benefitProperties={{
                indexStepDay: benefitProperties.transport.indexStepDay,
                stepNbDays: benefitProperties.transport.stepNbDays,
                benefitDisplays: benefitProperties.transport.standardDisplays,
                time: benefitProperties.transport.time,
              }}
              isViewMode={isViewMode}
            />
          )}
          {renderAssociatedBenefits(standardTransportAssociated)}
        </div>
        <div className="flex flex-col gap-2">
          {benefits.activities?.standardBenefit && (
            <CircuitEditorialBenefitElement
              isStandard
              type="Activités"
              title={getStandardBenefitNameFromBenefitType('activities')}
              onRemove={() =>
                setValue(
                  `circuitDays.${index}.steps.${stepIndex}.activities.standardBenefit`,
                  undefined,
                )
              }
              onLabelClick={() => openStepModal('Activités')}
              benefitProperties={{
                indexStepDay: benefitProperties.activities.indexStepDay,
                stepNbDays: benefitProperties.activities.stepNbDays,
                benefitDisplays: benefitProperties.activities.standardDisplays,
                time: benefitProperties.activities.time,
              }}
              isViewMode={isViewMode}
            />
          )}
          {renderAssociatedBenefits(standardActivitiesAssociated)}
        </div>
        <div className="flex flex-col gap-2">
          {benefits.meals?.standardBenefit && (
            <CircuitEditorialBenefitElement
              isStandard
              type="Repas"
              title={getStandardBenefitNameFromBenefitType('meals')}
              onRemove={() =>
                setValue(
                  `circuitDays.${index}.steps.${stepIndex}.meals.standardBenefit`,
                  undefined,
                )
              }
              onLabelClick={() => openStepModal('Repas')}
              benefitProperties={{
                indexStepDay: benefitProperties.meals.indexStepDay,
                stepNbDays: benefitProperties.meals.stepNbDays,
                benefitDisplays: benefitProperties.meals.standardDisplays,
                time: benefitProperties.meals.time,
              }}
              isViewMode={isViewMode}
            />
          )}
        </div>
        <div className="flex flex-col gap-2">
          {benefits.accommodations?.standardBenefit && (
            <CircuitEditorialBenefitElement
              isStandard
              type="Hébergements"
              title={getStandardBenefitNameFromBenefitType('accommodations')}
              onRemove={() =>
                removeStandardBenefit({
                  type: 'Hébergements',
                  stepId: benefitProperties.accommodations.id,
                })
              }
              onLabelClick={() => openStepModal('Hébergements')}
              benefitProperties={{
                indexStepDay: benefitProperties.accommodations.indexStepDay,
                stepNbDays: benefitProperties.accommodations.stepNbDays,
                benefitDisplays:
                  benefitProperties.accommodations.standardDisplays,
                time: benefitProperties.accommodations.time,
                pensionType:
                  benefitProperties.accommodations.standardPensionType,
              }}
              isViewMode={isViewMode}
            />
          )}
        </div>
      </div>
    </>
  ) : null;

  const getOptionalBenefitNameFromBenefitType = useCallback(
    (item: (typeof optionalBenefits.transport.benefits)[number]) => {
      return language
        ? (item.translations?.[language]?.name ?? item.name)
        : item.name;
    },
    [language, optionalBenefits],
  );

  const OptionalBenefitsSection =
    optionalLength > 0 ? (
      <>
        {!atLeastOneStandard && <HorizontalLine />}
        <Label className="text-gray-700">
          {t('circuits.editorial.sections.optional')}
        </Label>
        {/* Prestations optionnelles NON liées à un hébergement (transport /
            activités / repas). Colonnes par type ; la 4e colonne (hébergements)
            est vide ici, les hébergements sont rendus dans la grille alignée
            ci-dessous. */}
        {(optionalBenefits.transport.benefits.length > 0 ||
          optionalBenefits.activities.benefits.length > 0 ||
          optionalBenefits.meals.benefits.length > 0) && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex flex-col gap-2">
              {optionalBenefits.transport.benefits.map((item, benefitIndex) => (
                <CircuitEditorialBenefitElement
                  key={item.id}
                  type="Transport"
                  title={getOptionalBenefitNameFromBenefitType(item)}
                  onRemove={() => {
                    removeOptionalBenefits({
                      indexToRemove: benefitIndex,
                      optionalBenefits: optionalBenefits.transport.benefits,
                      stepId: benefitProperties.transport.id,
                      type: 'Transport',
                    });
                  }}
                  isViewMode={isViewMode}
                  onLabelClick={() => openStepModal('Transport')}
                  benefitProperties={{
                    indexStepDay: benefitProperties.transport.indexStepDay,
                    stepNbDays: benefitProperties.transport.stepNbDays,
                    benefitDisplays:
                      benefitProperties.transport.optionalDisplays,
                    time: benefitProperties.transport.time,
                  }}
                />
              ))}
            </div>
            <div className="flex flex-col gap-2">
              {optionalBenefits.activities.benefits.map(
                (item, benefitIndex) => (
                  <CircuitEditorialBenefitElement
                    key={item.id}
                    type="Activités"
                    title={getOptionalBenefitNameFromBenefitType(item)}
                    onRemove={() => {
                      removeOptionalBenefits({
                        indexToRemove: benefitIndex,
                        optionalBenefits: optionalBenefits.activities.benefits,
                        stepId: benefitProperties.activities.id,
                        type: 'Activités',
                      });
                    }}
                    onLabelClick={() => openStepModal('Activités')}
                    isViewMode={isViewMode}
                    benefitProperties={{
                      indexStepDay: benefitProperties.activities.indexStepDay,
                      stepNbDays: benefitProperties.activities.stepNbDays,
                      benefitDisplays:
                        benefitProperties.activities.optionalDisplays,
                      time: benefitProperties.activities.time,
                    }}
                  />
                ),
              )}
            </div>
            <div className="flex flex-col gap-2">
              {optionalBenefits.meals.benefits.map((item, benefitIndex) => (
                <CircuitEditorialBenefitElement
                  key={item.id}
                  type="Repas"
                  title={getOptionalBenefitNameFromBenefitType(item)}
                  onRemove={() => {
                    removeOptionalBenefits({
                      indexToRemove: benefitIndex,
                      optionalBenefits: optionalBenefits.meals.benefits,
                      stepId: benefitProperties.meals.id,
                      type: 'Repas',
                    });
                  }}
                  isViewMode={isViewMode}
                  onLabelClick={() => openStepModal('Repas')}
                  benefitProperties={{
                    indexStepDay: benefitProperties.meals.indexStepDay,
                    stepNbDays: benefitProperties.meals.stepNbDays,
                    benefitDisplays: benefitProperties.meals.optionalDisplays,
                    time: benefitProperties.meals.time,
                  }}
                />
              ))}
            </div>
            <div />
          </div>
        )}
        {/* Hébergements optionnels : une ligne de grille par hébergement, avec
            ses prestations associées alignées sur la même ligne (colonnes
            Transport / Activités). */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2">
          {optionalBenefits.accommodations.benefits.map(
            (item, benefitIndex) => (
              <Fragment key={item.id}>
                <div className="flex flex-col gap-2">
                  {renderAssociatedBenefits(
                    optionalAccommodationRows[benefitIndex]
                      ?.transportAssociated ?? [],
                    `optional-transport-${item.id}-`,
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {renderAssociatedBenefits(
                    optionalAccommodationRows[benefitIndex]
                      ?.activitiesAssociated ?? [],
                    `optional-activities-${item.id}-`,
                  )}
                </div>
                <div />
                <div className="flex flex-col gap-2">
                  <CircuitEditorialBenefitElement
                    type="Hébergements"
                    title={getOptionalBenefitNameFromBenefitType(item)}
                    onRemove={() => {
                      removeOptionalBenefits({
                        indexToRemove: benefitIndex,
                        optionalBenefits:
                          optionalBenefits.accommodations.benefits,
                        stepId: benefitProperties.accommodations.id,
                        type: 'Hébergements',
                      });
                    }}
                    onLabelClick={() => openStepModal('Hébergements')}
                    isViewMode={isViewMode}
                    benefitProperties={{
                      indexStepDay:
                        benefitProperties.accommodations.indexStepDay,
                      stepNbDays: benefitProperties.accommodations.stepNbDays,
                      benefitDisplays:
                        benefitProperties.accommodations.optionalDisplays,
                      time: benefitProperties.accommodations.time,
                      pensionType:
                        benefitProperties.accommodations
                          .optionalPensionsTypes?.[item.id],
                    }}
                  />
                </div>
              </Fragment>
            ),
          )}
        </div>
      </>
    ) : null;

  return (
    <>
      {StandardBenefitsSection}
      {OptionalBenefitsSection}
    </>
  );
};

export default CircuitEditorialStep;
